import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import { useLanguage } from "@/contexts/LanguageContext";

const SECTIONS_PT = [
  {
    title: "1. O que são Cookies?",
    paras: [
      "Cookies são pequenos ficheiros de texto armazenados no dispositivo do utilizador (computador, tablet ou telemóvel) quando visita um website. Servem para o website funcionar corretamente, memorizar preferências e, em alguns casos, recolher informação estatística ou de marketing.",
      "Tecnologias semelhantes, como o armazenamento local (localStorage), são também abrangidas por esta política.",
    ],
  },
  {
    title: "2. Cookies que Utilizamos",
    paras: ["A plataforma PASS Algarve utiliza as seguintes categorias:"],
    list: [
      "Estritamente necessários: gestão de sessão e autenticação do utilizador (login), segurança da conta e funcionamento do processo de compra. Não podem ser desativados.",
      "Preferências: memorização do idioma escolhido (PT/EN) e do consentimento de cookies.",
      "Pagamentos: a Stripe pode utilizar cookies próprios durante o checkout para processar o pagamento de forma segura e prevenir fraude.",
    ],
    parasAfter: [
      "Atualmente, o PASS Algarve não utiliza cookies de publicidade nem de análise estatística de terceiros. Caso venham a ser introduzidos, apenas serão ativados mediante consentimento prévio do utilizador e esta política será atualizada.",
    ],
  },
  {
    title: "3. Gestão de Cookies",
    paras: [
      "No primeiro acesso à plataforma, é apresentado um banner de consentimento que permite ao utilizador aceitar, recusar ou gerir as suas preferências de cookies.",
      "O utilizador pode ainda, a qualquer momento, gerir ou eliminar cookies através das definições do seu navegador. Note que a desativação de cookies estritamente necessários pode impedir o funcionamento correto da plataforma (por exemplo, o login e a compra).",
    ],
  },
  {
    title: "4. Prazos de Conservação",
    paras: [
      "Os cookies de sessão expiram quando o navegador é fechado ou a sessão termina. Os cookies e registos de preferências (idioma e consentimento) são conservados no dispositivo do utilizador até serem eliminados pelo próprio.",
    ],
  },
  {
    title: "5. Mais Informações",
    paras: [
      "Para informação detalhada sobre o tratamento de dados pessoais, consulte a Política de Privacidade do PASS Algarve.",
      "Para qualquer questão, contacte-nos através do email info@passalgarve.com.",
    ],
  },
];

const SECTIONS_EN = [
  {
    title: "1. What are Cookies?",
    paras: [
      "Cookies are small text files stored on the user's device (computer, tablet or mobile phone) when visiting a website. They allow the website to work properly, remember preferences and, in some cases, collect statistical or marketing information.",
      "Similar technologies, such as local storage (localStorage), are also covered by this policy.",
    ],
  },
  {
    title: "2. Cookies We Use",
    paras: ["The PASS Algarve platform uses the following categories:"],
    list: [
      "Strictly necessary: session management and user authentication (login), account security and operation of the purchase process. These cannot be disabled.",
      "Preferences: remembering the chosen language (PT/EN) and the cookie consent.",
      "Payments: Stripe may use its own cookies during checkout to process the payment securely and prevent fraud.",
    ],
    parasAfter: [
      "Currently, PASS Algarve does not use advertising cookies or third-party analytics cookies. If they are introduced in the future, they will only be activated with the user's prior consent and this policy will be updated.",
    ],
  },
  {
    title: "3. Managing Cookies",
    paras: [
      "On the first visit to the platform, a consent banner is shown allowing the user to accept, refuse or manage their cookie preferences.",
      "The user may also, at any time, manage or delete cookies through their browser settings. Please note that disabling strictly necessary cookies may prevent the platform from working properly (for example, login and purchase).",
    ],
  },
  {
    title: "4. Retention Periods",
    paras: [
      "Session cookies expire when the browser is closed or the session ends. Preference records (language and consent) are kept on the user's device until deleted by the user.",
    ],
  },
  {
    title: "5. More Information",
    paras: [
      "For detailed information about the processing of personal data, please see the PASS Algarve Privacy Policy.",
      "For any questions, contact us at info@passalgarve.com.",
    ],
  },
];

export default function CookiesPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const pt = language !== "en";
  const sections = pt ? SECTIONS_PT : SECTIONS_EN;

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif" }}>
      <SiteHeader active="" onBuyClick={() => navigate("/#passport")} />
      <Seo
        title={pt ? "Política de Cookies" : "Cookie Policy"}
        description={pt ? "Política de Cookies da plataforma PASS Algarve: que cookies utilizamos e como gerir o consentimento." : "PASS Algarve Cookie Policy: which cookies we use and how to manage consent."}
      />

      <section className="bg-[#071A35] pt-28 pb-12" data-testid="cookies-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#D4A85D] text-xs font-bold uppercase tracking-[0.25em] mb-3">PASS ALGARVE</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-3">
            {pt ? "Política de Cookies" : "Cookie Policy"}
          </h1>
          <p className="text-white/60 text-sm">
            {pt ? "Última atualização: 1 de setembro de 2026" : "Last updated: September 1, 2026"}
          </p>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-white flex-1" data-testid="cookies-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-10">
            {sections.map((s, i) => (
              <div key={i} data-testid={`cookies-section-${i + 1}`}>
                <h2 className="text-[#071A35] font-bold text-lg mb-3 pb-2 border-b border-[#D4A85D]/30">{s.title}</h2>
                <div className="text-[#071A35]/80 text-sm leading-relaxed space-y-3">
                  {s.paras.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                  {s.list && (
                    <ul className="list-none space-y-1.5 pl-1">
                      {s.list.map((li, k) => (
                        <li key={k} className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#D4A85D] mt-1.5 flex-shrink-0" />
                          {li}
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.parasAfter && s.parasAfter.map((p, j) => <p key={`a${j}`}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
